import type React from 'react';
import { MapControlButton } from '@/components/atoms/primitives';
import {
  MAP_CONTROLS_RIGHT_PX,
  MAP_LAYERS_PANEL_CONTROLS_GAP_PX,
  MAP_LAYERS_PANEL_WIDTH_PX,
} from '@/constants/mapLayout';
import { classNames } from '@/lib/utils';
import { useMapLayoutStore } from '@/stores/slices/useMapLayoutStore';
import type { MapControlsProps } from './MapControls.types';

// Panel widths for calculating offset
const NEWS_PANEL_WIDTH_PX = 350;
const AI_ASSISTANT_PANEL_WIDTH_PX = 350;

export const MapControls: React.FC<MapControlsProps> = ({
  onLocationClick,
  onGlobeClick,
  onRulerToggle,
  rulerOpen: _rulerOpen = false,
  className = '',
}) => {
  const layersPanelExpanded = useMapLayoutStore((state) => state.layersPanelExpanded);
  const newsPanelExpanded = useMapLayoutStore((state) => state.newsPanelExpanded);
  const aiAssistantPanelExpanded = useMapLayoutStore((state) => state.aiAssistantPanelExpanded);

  // Calculate right offset based on which panel is expanded (only one at a time)
  // Aligns with Mapbox NavigationControl which uses the same panel offset logic
  let right = MAP_CONTROLS_RIGHT_PX;
  if (layersPanelExpanded) {
    right = MAP_LAYERS_PANEL_WIDTH_PX + MAP_LAYERS_PANEL_CONTROLS_GAP_PX + MAP_CONTROLS_RIGHT_PX;
  } else if (newsPanelExpanded) {
    right = NEWS_PANEL_WIDTH_PX + MAP_LAYERS_PANEL_CONTROLS_GAP_PX + MAP_CONTROLS_RIGHT_PX;
  } else if (aiAssistantPanelExpanded) {
    right = AI_ASSISTANT_PANEL_WIDTH_PX + MAP_LAYERS_PANEL_CONTROLS_GAP_PX + MAP_CONTROLS_RIGHT_PX;
  }

  const controlContainerClassName =
    'group relative flex h-8 w-8 items-center justify-center rounded-lg border border-panel-border bg-panel-surface shadow-map-control backdrop-blur-sm hover:brightness-110';
  const controlButtonClassName =
    'flex h-full w-full items-center justify-center rounded-lg bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-border/70';
  const controlIconClassName = 'h-4 w-4 object-contain filter brightness-0 invert';

  return (
    <div className={classNames('absolute bottom-2 z-[3] flex flex-row gap-2', className)} style={{ right, transition: 'right 250ms' }}>
      <MapControlButton
        icon="location-black"
        tooltip="Current Location"
        onClick={onLocationClick}
        aria-label="Current Location"
        containerClassName={controlContainerClassName}
        buttonClassName={controlButtonClassName}
        iconClassName={controlIconClassName}
      />
      <MapControlButton
        icon="globe3-black"
        tooltip="Globe View"
        onClick={onGlobeClick}
        aria-label="Globe View"
        containerClassName={controlContainerClassName}
        buttonClassName={controlButtonClassName}
        iconClassName={controlIconClassName}
      />
      <MapControlButton
        icon="ruler-icon"
        tooltip="Distance Measurement"
        onClick={onRulerToggle}
        aria-label="Distance Measurement"
        containerClassName={controlContainerClassName}
        buttonClassName={controlButtonClassName}
        iconClassName={controlIconClassName}
      />
    </div>
  );
};
